<template>
  <div class="step-by-step">
    <horizontal-navigation
      v-if="!vertical"
      v-bind="stepsData"
      @goToStep="goToStep"/>
    <div class="step-by-step__body-container">
      <vertical-navigation
        v-if="vertical"
        v-bind="stepsData"
        @goToStep="goToStep"/>
      <div class="step-by-step__body-wrapper">
        <div class="step-by-step__body">
          <b-card v-if="baseDataLoader" class="step-by-step__loader-container">
            <b-container fluid>
              <s-loader class="text-center" size="lg" />
            </b-container>
          </b-card>
          <slot v-else :name="slotName" />
        </div>
        <bottom-navigation
          :loading="loading"
          :saveTemplate="saveTemplateButton"
          :dropdownButton="dropdownButton"
          :exportXlsxLoading="exportXlsxLoading"
          :saveTemplateLoading="saveTemplateLoading"
          v-bind="stepsData"
          @goToStep="goToStep"
          @conclude="conclude"
          @saveTemplate="saveTemplate"
          @exportXlsx="exportXlsx"

          @backToList="backToList"
        >
          <slot name="bottomPreviousPanel" slot="previousPanel"/>
          <slot name="bottomNextPanel" slot="nextPanel"/>
        </bottom-navigation>
      </div>
    </div>
  </div>
</template>
<script>
/*
  Uso do componente:

    <step-by-step
      ...
    >
      <div slot="content:0" />
      <div slot="content:stepName" />
      <div slot="content:2" />
    </step-by-step>

  Os slots são nomeados no formato "content:{id}", sendo a identificação:
    - Nome do passo se definido, senão;
    - Índice do passo;

  Outros slots existentes:
    - bottomPreviousPanel: junto do botão Voltar, após ele.
    - bottomNextPanel: junto do botão Próximo, antes ele.

  Eventos disparados:
    - next(evt), previous(evt): Ações de avançar e voltar. Podem ser cancelados por
      evt.preventDefault().
    - conclude(evt): botão conclude clicado. Não realiza nenhuma ação internamente.
      O evento vem de um botão de carregamento, possibilitando exibir ícone de carregamento.
      Ver LoadingButton para maiores informações.
 */
import HorizontalNavigation from './HorizontalNavigation';
import VerticalNavigation from './VerticalNavigation';
import BottomNavigation from './BottomNavigation';

export default {
  name: 'StepByStep',
  components: {
    HorizontalNavigation,
    VerticalNavigation,
    BottomNavigation,
  },
  props: {
    loading: {
      type: [Boolean, Number],
    },
    baseDataLoader: {
      type: Boolean,
      default: false,
    },
    /**
     * Array de configurações dos passos.
     * @prop {String} [label] Título do passo que será apresentado no cabeçalho.
     * @prop {String} [labelPath] Caminho para tradução do título que será apresentado no cabeçalho.
     * @prop {String} icon Classe CSS para o ícone no cabeçalho.
     * @prop {String} [name] Nome interno do passo. Também utilizado para identificação do slot de
     *  conteúdo do passo.
     * @prop {Function} [isDisabled] Função que avalia se o passo está habilitado ou não, para
     *  permitir navegação.
     * @prop {Object} [getConfirmMessageParams] Função que retorna as configurações do
     * $confirmMessage
     * @prop {Function} [canForward] Função que avalia se pode ser avançado para o próximo passo.
     * @prop {Function} [onForward] Função de evento ao avançar para o próximo passo. O avanço pode
     *  ser cancelado retornando false. Acontece antes do evento @next, suprimindo-o quando
     *  cancelado.
     */
    steps: Array,
    /**
     * Passo atual.
     */
    currentStep: {
      type: Number,
      default: 0,
    },
    /**
     * Label do botão voltar.
     */
    previousButtonText: {
      required: false,
      type: String,
    },
    /**
     * Label do botão avançar.
     */
    nextButtonText: {
      required: false,
      type: String,
    },
    /**
     * Label do botão salvar template.
     */
     saveTemplateButton: {
      type: Boolean,
      default: false,
    },
    /**
     * Status do botão de template.
     */
    //  statusTemplateSituation: {
    //   required: true,
    //   type: String,
    // },
    /**
     * Label do botão concluir.
     */
    concludeButtonText: {
      required: false,
      type: String,
    },
    /**
     * Desabilitar avanço.
     */
    nextDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Desabilitar retrocesso.
     */
    previousDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Desabilitar botão concluir.
     */
    concludeDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Valores:
     * full: permite avançar e voltar pelo cabeçalho
     * previous: permite apenas voltar pelo cabeçalho
     * disabled: não permite navegar pelo cabeçalho
     */
    headerNavigation: {
      type: String,
      default: 'previous',
    },
    /**
     * Define se o layout dos menus das abas será vertical ou horizontal
     */
    vertical: {
      type: Boolean,
      default: false,
    },
    dropdownButton: {
      type: Boolean,
      default: false,
    },
    saveTemplateLoading: {
      type: Boolean,
      default: false,
    },
    exportXlsxLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: 0,
    };
  },
  created() {
    this.changeStep(this.currentStep);
  },
  computed: {
    stepsData() {
      return {
        steps: this.steps,
        step: this.step,
        headerNavigation: this.headerNavigation,
        previousButtonText: this.previousButtonText,
        previousDisabled: this.previousDisabled,
        concludeButtonText: this.concludeButtonText,
        saveTemplateButton: this.saveTemplateButton,
        statusTemplateSituation: this.statusTemplateSituation,
        concludeDisabled: this.concludeDisabled,
        nextDisabled: this.nextDisabled,
        nextButtonText: this.nextButtonText,
      };
    },
    slotName() {
      return `content:${this.steps[this.step].name || this.step}`;
    },
  },
  methods: {
    goToStep(targetStep) {
      let { getConfirmMessageParams } = this.steps[this.step];
      getConfirmMessageParams = getConfirmMessageParams ? getConfirmMessageParams() : null;

      if (getConfirmMessageParams) {
        this.$confirmMessage(getConfirmMessageParams)
          .accepted(() => this.goToStepCallback(targetStep))
          .rejected(() => false);
        return;
      }
      this.goToStepCallback(targetStep);
    },
    changeStep(step) {
      if (step === this.step) {
        return;
      }
      if (step == null || step < 0) {
        this.step = 0;
      } else if (step >= this.steps.length) {
        this.step = this.steps.length - 1;
      } else {
        this.step = step;
      }
      this.$emit('update:currentStep', this.step);
    },
    conclude(event) {
      this.$emit('conclude', event);
    },
    saveTemplate(event) {
      this.$emit('saveTemplate', event);
    },
    exportXlsx(event) {
      this.$emit('exportXlsx', event);
    },
    backToList(event) {
      this.$emit('backToList', event);
    },
    fireStepChangeEvent(targetStep) {
      let continueAfterEvent = true;
      const eventName = (targetStep > this.step) ? 'next' : 'previous';

      if (eventName === 'next'
        && this.steps[this.step].onForward
        && this.steps[this.step].onForward() === false) {
        return false;
      }

      this.$emit(eventName, {
        currentStep: this.steps[this.step],
        currentStepNumber: this.step,
        targetStep: this.steps[targetStep],
        preventDefault() {
          continueAfterEvent = false;
        },
      });
      return continueAfterEvent;
    },
    goToStepCallback(targetStep) {
      if (this.fireStepChangeEvent(targetStep)) {
        this.changeStep(targetStep);
      }
    },
  },
  watch: {
    currentStep(newVal) {
      this.changeStep(newVal);
    },
  },
};
</script>
<style lang="sass" scoped>
@import src/styles/defaults/variables
@import "src/styles/defaults/mobile-first"
@import src/styles/defaults/mixins

.step-by-step
  border-radius: 5px
  display: flex
  flex-direction: column
  &__loader-container
    background-color: var(--color-background-regular)
    min-height: 500px
    display: flex
    flex-direction: row
    flex-wrap: nowrap
    justify-content: center
    align-content: stretch
    align-items: center
  +md-box-shadow-1
  &__body-container
    min-height: 630px
    background-color: var(--color-background-light)
    display: flex
    flex-direction: column
    overflow: visible
  &__body-wrapper
    overflow: visible
    border-bottom-left-radius: 5px
    border-bottom-right-radius: 5px
    flex: 1
    display: flex
    flex-direction: column
  &__body
    overflow-y: visible
    flex: 1
    padding: 20px 20px 0
  +media-min-lg()
    &__body-container
      flex-direction: row
  +media-min-md()
  +media-min-lg()
</style>
